import {DrupalNode, DrupalTaxonomyTerm} from "next-drupal";
import {Fragment, useEffect, useState} from "react";
import classNames from "classnames";
import {FormattedText} from "../formatted-text";
import {ImageIcon} from "../atoms/icon";

export function FAQItem({node, activeParent, singleCategory}: {node: DrupalNode, activeParent: boolean, singleCategory?: boolean}) {
  const [active, setActive] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    // Debounce an additional expanded state; so we don't collapse the
    // accordion content too soon, and un-rerender it after it has closed.
    const update = setTimeout(() => {
      setExpanded(active);
    }, active ? 0 : 200);

    return () => {
      clearTimeout(update);
    }
  }, [active]);

  /**
   * Create a simple triangle.
   *
   * @constructor
   */
  function Expander(): JSX.Element {
    return <span className={classNames(
      "inline-block pr-4 transition-all duration-500 ease-in-out text-2xl text-primary font-bold",
    )}>{active ? "-" : "+"}</span>
  }

  return (
    <button className={classNames(
      "faq-topic-link bg-white px-2 py-2 text-lg font-normal cursor-pointer border-l-4 transition-all w-full text-left",
      {"border-white": !activeParent},
      {"border-primary": activeParent},
      {"font-bold text-primary": active},
      {"lg:px-12": !singleCategory},
      {"lg:px-8": singleCategory}
    )} key={node.id} onClick={() => setActive(!active)} role="button">
      <div className="flex items-baseline">
        <Expander/>
        <div className="border-b border-hygienaDarkGray w-full pb-4">
          <div className="faq-topic-title">{node.title}</div>
          <div className={classNames(
            "overflow-hidden transition-all duration-200 px-0 lg:px-8",
            {"max-h-0": !active},
            {"max-h-screen": active},
          )}>
            <div className="pt-4 font-normal text-black">
              {expanded && node?.body?.processed && (
                <FormattedText processed={node.body.processed}/>
              )}
            </div>
          </div>
        </div>
      </div>
    </button>
  );
}

/**
 * Renders the FAQ items.
 *
 * @param items
 * @param childItems
 * @param singleCategory
 * @constructor
 */
export function FAQItems({items, childItems, singleCategory}: {items: DrupalNode[], childItems: any, singleCategory?: boolean}): JSX.Element {
  const [active, setActive] = useState(false);

  return (
    <>
      {items.map(item => (
        <FAQItem key={item.id} node={item} activeParent={active} singleCategory={singleCategory}/>
      ))}
      {Object.keys(childItems).map((name, idx) => {
        return (
          <Fragment key={`faq-${name}-${idx}`}>
            <div className={classNames(
              "bg-white px-7 py-2 text-primary font-bold text-lg cursor-pointer border-l-4 border-primary transition-all",
            )}>
              <div>{childItems[name].term.name}</div>
            </div>
            {childItems[name].items.map( child => (
              <FAQItem key={child.id} node={child} activeParent={active} />
            ))}
          </Fragment>
        );
      })}
    </>
  );
}

/**
 * Returns the FAQs for a single category without the accordion.
 *
 * @param items
 * @param childItems
 * @constructor
 */
export function FAQSingleContainer({items, childItems}: {items: DrupalNode[], childItems: any}): JSX.Element {
  return (
    <div className="cursor-pointer py-4">
      <FAQItems items={items} childItems={childItems} singleCategory={true}/>
    </div>
  );
}

/**
 * Returns FAQs categorized in an accordion.
 *
 * @param term
 * @param items
 * @param childItems
 * @constructor
 */
export function FAQContainer({term, items, childItems}: {term: DrupalTaxonomyTerm, items: DrupalNode[], childItems: any}): JSX.Element {
  const [active, setActive] = useState(false);

  /**
   * Create a simple triangle.
   *
   * @constructor
   */
  function Triangle({active}: {active: boolean}): JSX.Element {
    return <span className={classNames(
      "flex pr-4 transition-all duration-500 ease-in-out",
      {"transform rotate-90 -translate-x-5 translate-y-1.5": active},
    )}>
      <ImageIcon type={"chevron-right"} color={"black"} height={25} width={25} alt="Expand or Collapse"/>
    </span>
  }

  return (
    <div className={classNames("cursor-pointer")}>
      <button className="flex w-full h-full py-5" onClick={() => setActive(!active)} role="button">
        <h3 className="px-8 text-left font-light flex-grow">{term.name}</h3>
        <Triangle active={active}/>
      </button>
      <div className={classNames(
        "overflow-hidden transition-all delay-200 ease-linear origin-top py-4",
        {"hidden": !active},
      )}>
        <FAQItems items={items} childItems={childItems}/>
      </div>
    </div>
  );
}

export function FAQSection({faqs}: {
  faqs: DrupalNode[],
}) {
  const categories = {};
  const termsAdded: string[] = [];
  const faqCategoryTerms: DrupalTaxonomyTerm[] = [];

  for (const faq of faqs) {
    if (!faq?.field_faq_category) continue;
    for (const term of faq.field_faq_category) {
      if (!termsAdded.includes(term.id)) {
        termsAdded.push(term.id);
        faqCategoryTerms.push(term);
      }
    }
  }

  // Build out a 2 layer level of FAQ categories.
  // @todo there is a better way to structure this.
  for (const term of faqCategoryTerms) {
    const parent = term?.parent?.[0];
    if (parent?.id && parent.id !== "virtual") {
      if (!categories[parent.name] || Array.isArray(categories[parent.name])) {
        categories[parent.name] = {term: parent, children: {}, items: []};
      }
      categories[parent.name].children[term.name] = {term, items: []};
    }
    else {
      if (!categories[term.name]) {
        categories[term.name] = {term: term, children: {}, items: []};
      }
    }
  }

  // Build out a category map of FAQs.
  for (const item of faqs) {
    if (item?.field_faq_category) {
      for (const category of item.field_faq_category) {
        const parent = category?.parent?.[0];
        if (parent?.id !== "virtual") {
          categories[parent.name].children[category.name].items.push(item);
        }
        else {
          categories[category.name].items.push(item);
        }
      }
    }
  }

  const categoryCount = Object.keys(categories ?? {})?.length;

  return (
    <div className="paragraph--faqs container mx-auto drop-shadow-sm bg-white rounded-md">
      {categoryCount > 1 && Object.keys(categories).sort().map((key, index) => {
        if (!categories[key].items.length && !Object.keys(categories[key].children).length) {
          return;
        }
        return <FAQContainer key={key} term={categories[key].term} items={categories[key].items}
                             childItems={categories[key].children}/>
      })}
      {categoryCount === 1 && Object.keys(categories).sort().map((key, index) => {
        if (!categories[key].items.length && !Object.keys(categories[key].children).length) {
          return;
        }
        return <FAQSingleContainer key={key} items={categories[key].items}
                             childItems={categories[key].children}/>
      })}
    </div>
  )
}